import { CONTENT } from '../content/runtime.js';
import { CONFIG } from './config.js';
import { AIRPORTS } from './airports.js';

/**
 * Airports with a colour-coded map bring their whole floor plan. The layout values the game reads (ENTRANCE,
 * GATES, CONFIG.stages positions...) are shared objects, so switching airports rewrites their contents in place:
 * every importer sees the current airport without threading an airport id through each call.
 */
type Layout = Record<string, unknown>;
const layout = CONTENT.layout as Layout;
const SHARED: Layout = structuredClone(layout);
const SHARED_STAGES = CONFIG.stages.map(({ x, y }) => ({ x, y, facing: [0, 1] }));
let active = '';
const listeners: (() => void)[] = [];
/** Run after every floor-plan switch (caches of layout-derived points). */
export function onLayoutChange(listener: () => void) { listeners.push(listener); }

function replace(target: unknown, value: unknown) {
    if (Array.isArray(target) && Array.isArray(value)) { target.splice(0, target.length, ...structuredClone(value)); return true; }
    if (target && typeof target === 'object' && value && typeof value === 'object') {
        for (const key of Object.keys(target)) delete (target as Layout)[key];
        Object.assign(target, structuredClone(value));
        return true;
    }
    return false;
}

/** The floor plan an airport plays on: its map's own layout, or the shared plan. */
export function layoutFor(code: string): Layout {
    const own = AIRPORTS.find(a => a.code === code)?.gateLayout as Layout | undefined;
    return own && 'STAGES' in own ? own : SHARED;
}

/** Make `code`'s floor plan the active one (a no-op when it already is). */
export function applyLayout(code: string) {
    if (active === code) return;
    active = code;
    const next = layoutFor(code);
    for (const [key, value] of Object.entries(SHARED)) {
        const target = layout[key], wanted = key in next ? next[key] : value;
        if (!replace(target, wanted)) layout[key] = structuredClone(wanted);
    }
    // Keys only a map brings (OUTLINE, ZONES, STAGES...) come and go with it.
    for (const key of Object.keys(layout)) if (!(key in SHARED) && !(key in next)) delete layout[key];
    for (const [key, value] of Object.entries(next)) if (!(key in SHARED)) layout[key] = structuredClone(value);
    const stages = (next.STAGES as { x: number; y: number; facing: number[] }[] | undefined) ?? SHARED_STAGES;
    CONFIG.stages.forEach((stage, index) => Object.assign(stage, { x: stages[index].x, y: stages[index].y, facing: [...stages[index].facing] }));
    for (const listener of listeners) listener();
}

export function activeLayout() { return active; }
/** The airport whose floor plan is in effect; '' before any switch, while the shared plan (and its gates) is. */
export function activeAirport(): string {
    return active;
}

/** Run `work` on airport `code`'s floor plan (L1 facts for an airport you are not standing in), then switch back. */
export function withLayout<T>(code: string, work: () => T): T {
    const previous = active;
    if (previous === code) return work();
    applyLayout(code);
    try { return work(); } finally { if (previous) applyLayout(previous); }
}
